import React from 'react';
import { Link } from 'gatsby';
import clsx from 'clsx';
import { useLocation } from '@reach/router';
import {
	objectToSearchString,
	searchStringToObject,
} from 'serialize-query-params';
import './style.css';

type Props = {
	variant?:
		| 'primary'
		| 'primaryGradient'
		| 'primaryProduct'
		| 'ghost'
		| 'secondary'
		| 'xeroPrimary'
		| 'xeroSecondary'
		| 'xemploPrimary'
		| 'xemploSecondary'
		| 'myob'
		| 'neutral'
		| 'navy'
		| 'blue'
		| 'aqua';
	external?: boolean;
	className?: string;
	size?: 'small' | 'large';
	shape?: 'rounded' | 'rectangle';
	useDefaultButtonStyle?: boolean;
	children: any;
	path: string;
	onClick?: () => void;
};

export const VARIANT_MAPPING = {
	primary: '',
	primaryGradient: 'btn--gradient',
	primaryProduct: 'btn--product',
	ghost: 'btn--ghost',
	secondary: 'btn--secondary',
	xeroPrimary: 'btn--xero',
	xeroSecondary: 'btn--xero--secondary',
	xemploPrimary: 'btn--xemplo',
	xemploSecondary: 'btn--xemplo--secondary',
	myob: 'btn--myob',
	neutral: 'btn--neutral',
	aqua: 'btn--aqua',
	navy: 'btn--navy',
	blue: 'btn--blue',
};

export const Button = ({
	variant = 'aqua',
	external = false,
	className,
	children,
	size,
	shape = 'rounded',
	useDefaultButtonStyle = true,
	path = '/',
	onClick,
}: Props) => {
	const { search } = useLocation();
	const searchParams = searchStringToObject(search);
	const conditions = ['utm_medium', 'utm_source', 'utm_campaign'];
	const utmObject = Object.fromEntries(
		Object.entries(searchParams).filter(([key]) =>
			conditions.some((str) => key.includes(str))
		)
	);
	const stringifiedUtmParameters = objectToSearchString(utmObject);

	const urlWithUtmParameters = `${path}${
		stringifiedUtmParameters ? `?${stringifiedUtmParameters}` : ''
	}`;

	if (!external) {
		return onClick ? (
			<button
				className={clsx(
					{ [VARIANT_MAPPING[variant]]: !!variant },
					{ btn: useDefaultButtonStyle },
					{ 'btn--small': size === 'small' },
					{ 'btn--large': size === 'large' },
					{ 'btn--rectangle': shape === 'rectangle' },
					className
				)}
				onClick={
					onClick
						? (e) => {
								e.preventDefault();
								onClick();
						  }
						: undefined
				}
			>
				{children}
			</button>
		) : (
			<Link
				to={path}
				className={clsx(
					{ [VARIANT_MAPPING[variant]]: !!variant },
					{ btn: useDefaultButtonStyle },
					{ 'btn--small': size === 'small' },
					{ 'btn--large': size === 'large' },
					{ 'btn--rectangle': shape === 'rectangle' },
					className
				)}
			>
				{children}
			</Link>
		);
	}

	return (
		<a
			href={urlWithUtmParameters}
			className={clsx(
				{ [VARIANT_MAPPING[variant]]: !!variant },
				{ btn: useDefaultButtonStyle },
				{ 'btn--small': size === 'small' },
				{ 'btn--large': size === 'large' },
				{ 'btn--rectangle': shape === 'rectangle' },
				className
			)}
			target="_blank"
		>
			{children}
		</a>
	);
};
